/** Extended MediaControlBar that auto-hides low-priority controls on small containers. */

import { MediaControlBar } from 'media-chrome';

/** Control priorities - higher values stay visible longer. */
const CONTROL_PRIORITIES: Record<string, number> = {
  'media-play-button': 100,
  'bunny-media-time-range': 95,
  'media-mute-button': 90,
  'media-fullscreen-button': 85,
  'bunny-settings-menu-button': 80,
  'media-time-display': 70,
  'bunny-time-display': 70,
  // The one way to jump to the live edge, so it outlasts the time display.
  'bunny-live-indicator': 75,
  'media-airplay-button': 65,
  'media-cast-button': 65,
  'media-pip-button': 60,
  'media-volume-range': 40,
  'media-captions-button': 35,
  'media-seek-backward-button': 30,
  'media-seek-forward-button': 30,
};

const DEFAULT_PRIORITY = 50;

interface HiddenControl {
  element: HTMLElement;
  hiddenAtWidth: number;
}

class BunnyMediaControlBar extends MediaControlBar {
  #resizeObserver: ResizeObserver | null = null;
  #hiddenControls: HiddenControl[] = [];
  /** Width at which compressed mode was entered, or null when not compressed. */
  #compressedAtWidth: number | null = null;
  #adjustRafId: number | null = null;
  #isAdjusting = false;

  override connectedCallback() {
    super.connectedCallback?.();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    // Observe both the bar and its children. Children are observed so that when
    // custom elements upgrade from 0px to their real size we re-run #adjust —
    // otherwise the bar's own width never changes and the ResizeObserver would
    // never fire after the initial (too-early) rAF call.
    this.#resizeObserver = new ResizeObserver(() => {
      const width = this.clientWidth;
      if (!width || !isFinite(width) || width <= 0) return;
      this.#scheduleAdjust(width);
    });
    this.#resizeObserver.observe(this);
    for (const child of this.children) {
      if (child instanceof HTMLElement) this.#resizeObserver.observe(child);
    }

    requestAnimationFrame(() => {
      const width = this.clientWidth;
      if (width > 0) this.#adjust(width);
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#adjustRafId !== null) {
      cancelAnimationFrame(this.#adjustRafId);
      this.#adjustRafId = null;
    }
  }

  // Use requestAnimationFrame instead of setTimeout so that:
  // (a) during continuous drag, #adjust fires once per frame rather than never
  //     (setTimeout resets on every call and never fires during rapid resize);
  // (b) measurements are taken after layout, before paint.
  #scheduleAdjust(width: number) {
    this.#lastScheduledWidth = width;
    if (this.#adjustRafId !== null) return;
    this.#adjustRafId = requestAnimationFrame(() => {
      this.#adjustRafId = null;
      const w = this.#lastScheduledWidth ?? this.clientWidth;
      if (w > 0) this.#adjust(w);
    });
  }

  #lastScheduledWidth: number | null = null;

  #getPriority(el: Element): number {
    return CONTROL_PRIORITIES[el.tagName.toLowerCase()] ?? DEFAULT_PRIORITY;
  }

  #isControlElement(el: Element): el is HTMLElement {
    if (!(el instanceof HTMLElement)) return false;
    if (this.#isFlexFiller(el)) return false;
    if (el.tagName === 'DIV' || el.tagName === 'SLOT') return false;
    return true;
  }

  #isFlexFiller(el: Element): boolean {
    if (!(el instanceof HTMLElement)) return false;
    return el.classList.contains('spacer') ||
      el.classList.contains('small-bar-time-range');
  }

  /**
   * Returns the minimum width reserved for a non-spacer flex filler (e.g. inline seek bar).
   * Spacers have min-width 0 so they don't reserve space; the inline time range has a CSS
   * min-width that must remain available for it to be usable.
   * Call once per adjust cycle and pass the result through — getComputedStyle is expensive.
   */
  #getFillerMinWidth(): number {
    for (const child of this.children) {
      if (
        child instanceof HTMLElement &&
        this.#isFlexFiller(child) &&
        !child.classList.contains('spacer') &&
        !(child.offsetWidth === 0 && child.offsetHeight === 0)
      ) {
        return parseFloat(getComputedStyle(child).minWidth) || 0;
      }
    }
    return 0;
  }

  /** Calculate minimum content width. Can't use scrollWidth - flexbox shrinks to fit. */
  #getContentWidth(
    gap: number,
    paddingLeft: number,
    paddingRight: number,
  ): number {
    let totalWidth = 0;
    // Count ALL visible flex items (including spacers) for accurate gap calculation.
    // CSS flexbox gap applies between all adjacent flex items, not just controls.
    let flexItemCount = 0;

    for (const child of this.children) {
      if (!(child instanceof HTMLElement)) continue;

      // Use offsetWidth/offsetHeight check - elements with display:none have 0 dimensions
      // This avoids expensive getComputedStyle calls
      if (child.offsetWidth === 0 && child.offsetHeight === 0) continue;

      flexItemCount++;

      // Spacers participate in gap calculation but their width is intentionally
      // ignored (they use flex-grow to fill available space).
      if (this.#isFlexFiller(child)) continue;

      totalWidth += child.offsetWidth;
    }

    if (flexItemCount === 0) return 0;

    if (flexItemCount > 1) {
      totalWidth += gap * (flexItemCount - 1);
    }

    totalWidth += paddingLeft + paddingRight;

    return totalWidth;
  }

  #isOverflowing(
    gap: number,
    paddingLeft: number,
    paddingRight: number,
    fillerMinWidth: number,
  ): boolean {
    return this.#getContentWidth(gap, paddingLeft, paddingRight) >
      this.clientWidth - fillerMinWidth;
  }

  #getVisibleControls(): HTMLElement[] {
    const hiddenSet = new Set(this.#hiddenControls.map((h) => h.element));

    return [...this.children].filter((el): el is HTMLElement => {
      if (!this.#isControlElement(el)) return false;
      if (hiddenSet.has(el)) return false;
      // Avoid getComputedStyle - elements with display:none have 0 dimensions
      return !(el.offsetWidth === 0 && el.offsetHeight === 0);
    });
  }

  /** Set or remove `compressed` on every control child. */
  #setCompressedMode(enabled: boolean) {
    for (const child of this.children) {
      if (!this.#isControlElement(child)) continue;
      if (enabled) {
        child.setAttribute('compressed', '');
      } else {
        child.removeAttribute('compressed');
      }
    }
  }

  #adjust(containerWidth: number) {
    if (this.#isAdjusting || !this.isConnected) return;

    this.#isAdjusting = true;
    try {
      // Cache computed styles once per adjust cycle to avoid repeated getComputedStyle calls
      const computedStyle = getComputedStyle(this);
      const gap = parseFloat(computedStyle.gap) || 0;
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
      const fillerMinWidth = this.#getFillerMinWidth();

      this.#cleanupStaleReferences();
      this.#tryRestore(
        containerWidth,
        gap,
        paddingLeft,
        paddingRight,
        fillerMinWidth,
      );
      this.#tryDecompress(
        containerWidth,
        gap,
        paddingLeft,
        paddingRight,
        fillerMinWidth,
      );
      this.#hideUntilFits(
        containerWidth,
        gap,
        paddingLeft,
        paddingRight,
        fillerMinWidth,
      );
    } finally {
      this.#isAdjusting = false;
    }
  }

  #cleanupStaleReferences() {
    this.#hiddenControls = this.#hiddenControls.filter(
      ({ element }) => element.isConnected && this.contains(element),
    );
  }

  /**
   * Attempts to restore previously hidden controls when container width increases.
   * Controls are restored in order of the width at which they were hidden (largest first),
   * meaning controls hidden at wider widths get priority for restoration.
   */
  #tryRestore(
    containerWidth: number,
    gap: number,
    paddingLeft: number,
    paddingRight: number,
    fillerMinWidth: number,
  ) {
    if (this.#hiddenControls.length === 0) return;

    const candidates = [...this.#hiddenControls].sort(
      (a, b) => b.hiddenAtWidth - a.hiddenAtWidth,
    );

    for (const { element, hiddenAtWidth } of candidates) {
      if (!element.isConnected) continue;

      // 10px hysteresis prevents flickering when width hovers near the threshold.
      // Without this buffer, controls would rapidly show/hide at boundary widths.
      if (containerWidth > hiddenAtWidth + 10) {
        element.style.display = '';
        this.#hiddenControls = this.#hiddenControls.filter(
          (h) => h.element !== element,
        );

        if (
          this.#isOverflowing(
            gap,
            paddingLeft,
            paddingRight,
            fillerMinWidth,
          )
        ) {
          element.style.display = 'none';
          this.#hiddenControls.push({ element, hiddenAtWidth: containerWidth });
          break;
        }
      }
    }
  }

  /**
   * Exits compressed mode when all hidden controls have been restored and there is
   * enough space. Only attempted once no controls are hidden, so controls are always
   * fully visible before the bar tries to expand them back to their full size.
   */
  #tryDecompress(
    containerWidth: number,
    gap: number,
    paddingLeft: number,
    paddingRight: number,
    fillerMinWidth: number,
  ) {
    if (this.#compressedAtWidth === null) return;
    if (this.#hiddenControls.length > 0) return;

    // 10px hysteresis mirrors the restore logic.
    if (containerWidth > this.#compressedAtWidth + 10) {
      this.#setCompressedMode(false);
      if (
        this.#isOverflowing(
          gap,
          paddingLeft,
          paddingRight,
          fillerMinWidth,
        )
      ) {
        // Decompressing caused overflow — re-enter compressed mode.
        this.#setCompressedMode(true);
        this.#compressedAtWidth = containerWidth;
      } else {
        this.#compressedAtWidth = null;
      }
    }
  }

  /**
   * Hides controls until content fits within the container width.
   * On first overflow, sets `compressed` on all controls simultaneously so every
   * component can shrink itself (e.g. drop duration text). Only if still overflowing
   * after that does it start hiding individual controls by priority.
   */
  #hideUntilFits(
    containerWidth: number,
    gap: number,
    paddingLeft: number,
    paddingRight: number,
    fillerMinWidth: number,
  ) {
    if (
      !this.#isOverflowing(
        gap,
        paddingLeft,
        paddingRight,
        fillerMinWidth,
      )
    ) return;

    // Phase 1: enter compressed mode (all controls shrink) if not already in it.
    if (this.#compressedAtWidth === null) {
      this.#setCompressedMode(true);
      this.#compressedAtWidth = containerWidth;
      if (
        !this.#isOverflowing(
          gap,
          paddingLeft,
          paddingRight,
          fillerMinWidth,
        )
      ) return;
    }

    // Phase 2: hide individual controls by priority until content fits.
    const visible = this.#getVisibleControls()
      .map((el) => ({ el, priority: this.#getPriority(el) }))
      .sort((a, b) => a.priority - b.priority);

    // maxIterations prevents infinite loops if isOverflowing() has edge case bugs.
    const maxIterations = visible.length;
    let iterations = 0;

    // Keep at least one control visible (visible.length > 1) so the control bar
    // is never completely empty, even if overflow persists at very small widths.
    while (
      this.#isOverflowing(gap, paddingLeft, paddingRight, fillerMinWidth) &&
      visible.length > 1 && iterations < maxIterations
    ) {
      const toHide = visible.shift()!;
      toHide.el.style.display = 'none';
      this.#hiddenControls.push({
        element: toHide.el,
        hiddenAtWidth: containerWidth,
      });
      iterations++;
    }
  }
}

if (!globalThis.customElements.get('bunny-media-control-bar')) {
  globalThis.customElements.define(
    'bunny-media-control-bar',
    BunnyMediaControlBar,
  );
}

export { BunnyMediaControlBar, CONTROL_PRIORITIES };
